/**
 * TapLandingPage.tsx
 *
 * Public landing page for the front-desk tap pad link, "/tap/:deskToken".
 * Not behind auth.
 *
 * When a patient holds their phone to the pad, the phone reads this URL. With
 * the Next Health app installed and Universal Links in place, the OS hands the
 * link to the app and this page is never seen. It renders when the app is not
 * installed (or an older build has no Universal Links): it points the patient
 * at the app and, failing that, at the front desk.
 *
 * Static on purpose: no API call, and the desk token is never shown as text —
 * it only appears inside the optional deep-link href (see lib/tapLanding.ts).
 * Root wrapper copied from CheckInPage.tsx, the other public patient-facing
 * page.
 */
import { useParams } from 'react-router-dom';
import { Smartphone } from 'lucide-react';

import { APP_SCHEME, STORE_LINKS, openAppHref } from '@/lib/tapLanding';

export default function TapLandingPage() {
  const { deskToken } = useParams<{ deskToken: string }>();
  const appHref = openAppHref(APP_SCHEME, deskToken ?? '');

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-slate-100 flex flex-col items-center justify-center px-4 py-12">
      {/* Branding */}
      <div className="text-center mb-10">
        <h1 className="text-4xl font-extrabold text-indigo-700 tracking-tight">
          Prospera AI
        </h1>
        <p className="mt-2 text-xl text-slate-500 font-medium">Patient Check-In</p>
      </div>

      {/* Card */}
      <div className="w-full max-w-2xl mx-auto bg-white border border-slate-200 rounded-2xl shadow-xl p-8">
        <div className="space-y-6 text-center">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-indigo-100">
            <Smartphone className="h-8 w-8 text-indigo-600" aria-hidden="true" />
          </div>

          <h2 className="text-2xl font-semibold text-slate-900">
            Check in with the Next Health app
          </h2>
          <p className="text-base text-slate-600">
            Open the Next Health app on this phone and hold it to the pad again. The app lets the
            front desk know you have arrived.
          </p>

          {appHref && (
            <a
              href={appHref}
              className="block w-full bg-indigo-600 hover:bg-indigo-700 text-white py-4 px-8 text-lg font-semibold rounded-xl transition-colors"
            >
              Open the app
            </a>
          )}

          <div className="space-y-3">
            <p className="text-sm text-slate-500">Don&apos;t have the app yet?</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a
                href={STORE_LINKS.ios}
                className="inline-flex items-center justify-center rounded-xl border border-slate-300 bg-white px-5 py-3 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                Get it on the App Store
              </a>
              <a
                href={STORE_LINKS.android}
                className="inline-flex items-center justify-center rounded-xl border border-slate-300 bg-white px-5 py-3 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                Get it on Google Play
              </a>
            </div>
          </div>

          <p className="text-sm text-slate-500 border-t border-slate-200 pt-5">
            Not using the app? Please check in at the front desk.
          </p>
        </div>
      </div>
    </div>
  );
}
